import { CallAPI } from "../../apiManager/ApiCall";
import {
  adminPackagesURL,
  adminDiscountUsersURL,
} from "apiManager/APIConfig";

/*
  Admin Packages dashboard — the full catalog of in-app-purchase
  packages across every platform (iOS / Android / Web combos) and
  every language, in one shot. The mobile app's in-app-purchase
  screen only sees the rows matching its current type+lang, but
  admins need to manage the whole tree from a single screen.

  The server returns each Package row with an embedded
  `translations[]` array of PackageTranslation rows. The base
  language (Package.lang) is implicit on the Package row itself.

  Set includeDeleted=true to also receive soft-deleted rows (so
  admins can audit what was removed); default omits them.
*/
export const GetAdminPackagesCall = async ({ includeDeleted = false } = {}) => {
  const params = new URLSearchParams();
  if (includeDeleted) params.set("include_deleted", "true");
  const query = params.toString();
  try {
    const response = await CallAPI({
      url: query ? `${adminPackagesURL}?${query}` : adminPackagesURL,
      method: "GET",
    });
    if (response?.status === 200 && Array.isArray(response?.data)) {
      return { packages: response.data };
    }
    return { packages: [] };
  } catch (_) {
    return { packages: [] };
  }
};

/*
  Edit a package. `body` may include shared fields (price, type,
  product_id, expire_limit, identifier, discount_code) plus a
  `translations: [{ lang, name, description, package_title }]`
  array; the server applies each translation as an upsert keyed by
  (package_id, lang) and writes the base-language translation back
  to the Package row itself so the mobile app stays in sync.
*/
export const AdminEditPackageCall = async (id, body) => {
  if (!id) return { ok: false };
  try {
    const response = await CallAPI({
      url: `${adminPackagesURL}/${id}`,
      method: "PUT",
      body,
    });
    if (response?.status === 200 && response?.data) {
      return { ok: true, package: response.data };
    }
    return { ok: false, message: response?.message || "Update failed." };
  } catch (_) {
    return { ok: false, message: "Network error." };
  }
};

export const AdminDeletePackageCall = async (id) => {
  if (!id) return { ok: false };
  try {
    const response = await CallAPI({
      url: `${adminPackagesURL}/${id}`,
      method: "DELETE",
    });
    if (response?.status === 200) return { ok: true };
    return { ok: false, message: response?.message || "Delete failed." };
  } catch (_) {
    return { ok: false, message: "Network error." };
  }
};

/*
  Admin Discount users — list every earner who has opted into the
  earn-money program AND owns at least one AffiliateCode row. Each
  user object surfaces their discount code(s) and the packages those
  codes currently swap into the paywall.
*/
export const GetAdminDiscountUsersCall = async () => {
  try {
    const response = await CallAPI({
      url: adminDiscountUsersURL,
      method: "GET",
    });
    if (response?.status === 200 && Array.isArray(response?.data)) {
      return { users: response.data };
    }
    return { users: [] };
  } catch (_) {
    return { users: [] };
  }
};

/*
  Replace the discounted packages assigned to one earner. Body:
    { packageIds: [<uuid>, ...], code?: string, commission_rate?: number }
  Returns the canonical code the server settled on (useful for
  rendering after the admin lets the backend generate a new one).
*/
export const AdminUpdateDiscountUserAssignmentsCall = async (userId, body) => {
  if (!userId) return { ok: false };
  try {
    const response = await CallAPI({
      url: `${adminDiscountUsersURL}/${userId}/assignments`,
      method: "PUT",
      body,
    });
    if (response?.status === 200) {
      return { ok: true, code: response?.data?.code || null };
    }
    return { ok: false, message: response?.message || "Update failed." };
  } catch (_) {
    return { ok: false, message: "Network error." };
  }
};
